import { CIActionBtnGroup, CIStatusCapsule, CITableGrid, Loader, NoDataAvailable, UnableToFetch, Unathourized } from 'ccs-ui-components';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getScheduleList } from '../../../../store/_api';
import { ApiModifySort, DataLoadingState, IGetJobDetails, IScheduleListProps, SortLabels } from '../../utilities/TypeDefs';
import classes from './ScheduleList.module.scss';

export const ScheduleList = ({ flag, deleteScheduler, searchTerm, journeyName, journeyVersion, page, setPage }: IScheduleListProps) => {
  // state variables
  const [scheduledJobList, setScheduledJobList] = useState<IGetJobDetails[]>([]);
  const [apiResponseStatus, setApiResponseStatus] = useState<DataLoadingState>(DataLoadingState.LOADING);
  const [apiStatusCode, setApiStatusCode] = useState<number>();

  const [sortedName, setSortedName] = useState(SortLabels.SCHEDULER_NAME);
  const [sortParameter, setSortParameter] = useState<string>();
  const [item, setItem] = useState<number>(10);
  const [listLength, setListLength] = useState<number>(0);
  const [sortedOrder, setSortedOrder] = useState(ApiModifySort.DESC);
  const [createdBySort, setCreatedBySort] = useState<string>('');

  const [schedulerNameSort, setSchedulerNameSort] = useState<string>(
    ApiModifySort.DESC
  );
  const [frequencySort, setFrequencySort] = useState<string>(
    ApiModifySort.DESC
  );

  // hooks
  const { t: translate } = useTranslation();

  // local variables
  const debounceDelay = 100;
  const activeStatus = 'active';

  /**
   * @method onGetPageHandler
   * @description handles the pages of the list (previous page or next page)
   * @param pageNumber
   */
  const onGetPageHandler = (pageNumber: number) => {
    setPage(pageNumber);
  };

  /**
   * @method onGetItemsHandler
   * @description handles the item to show in the page
   * @param itemNumber
   */
  const onGetItemsHandler = (itemNumber: number) => {
    setItem(itemNumber);
  };

  /**
   * @method debounceHandler
   * @description handles delay after searching the filtered job names
   */
  const debounceHandler = useCallback(_.debounce(async (value: string) => {
    setApiResponseStatus(DataLoadingState.LOADING);
    const resp = await getScheduleList({ jobSearchKey: value, journeyName, journeyVersion }, page, item, sortParameter ?? `${SortLabels.LAST_MODIFIED},${ApiModifySort.DESC}`);
    setApiStatusCode(resp.statusCode);
    if (resp.jobDetails) {
      setScheduledJobList(resp.jobDetails);
      setListLength(resp.totalJobCount);
    }
    setApiResponseStatus(DataLoadingState.COMPLETED);
  }, debounceDelay), [page, item, sortParameter]);

  /**
 * @method jobNameSortToggleHandler
 * @description sorts the list by job name
 */
  const jobNameSortToggleHandler = () => {
    setSortedName(SortLabels.SCHEDULER_NAME);
    if (schedulerNameSort === ApiModifySort.ASC) {
      setSchedulerNameSort(ApiModifySort.DESC);
      setSortedOrder(ApiModifySort.DESC);
      setSortParameter(`${SortLabels.SCHEDULER_NAME},DESC`);
    } else {
      setSchedulerNameSort(ApiModifySort.ASC);
      setSortedOrder(ApiModifySort.ASC);
      setSortParameter(`${SortLabels.SCHEDULER_NAME},ASC`);
    }
  };

  /**
* @method frequencySortToggleHandler
* @description sorts the list by job name
*/
  const frequencySortToggleHandler = () => {
    setSortedName(SortLabels.SCHEDULER_FREQUENCY);
    if (frequencySort === ApiModifySort.ASC) {
      setFrequencySort(ApiModifySort.DESC);
      setSortedOrder(ApiModifySort.DESC);
      setSortParameter(`${SortLabels.SCHEDULER_FREQUENCY},DESC`);
    } else {
      setFrequencySort(ApiModifySort.ASC);
      setSortedOrder(ApiModifySort.ASC);
      setSortParameter(`${SortLabels.SCHEDULER_FREQUENCY},ASC`);
    }
  };

  /**
  * @method updatedBySortToggleHandler
  * @description sorts the list by created by (Ascending or Descending)
  */
  const updatedBySortToggleHandler = () => {
    setSortedName(SortLabels.UPDATED_BY);
    if (createdBySort === ApiModifySort.ASC) {
      setCreatedBySort(ApiModifySort.DESC);
      setSortedOrder(ApiModifySort.DESC);
      setSortParameter(`${SortLabels.UPDATED_BY},DESC`);
    } else {
      setCreatedBySort(ApiModifySort.ASC);
      setSortedOrder(ApiModifySort.ASC);
      setSortParameter(`${SortLabels.UPDATED_BY},ASC`);
    }
  };

  const footerOptionsObj = {
    itemsPerPage: item,
    currentPageNumber: page,
    numberOfPages: Math.max(1, Math.ceil(listLength / item)),
    onCurrentPageNumberChange: onGetPageHandler,
    onItemsPerPageChange: onGetItemsHandler
  };

  const footerObj = { footerType: 'paging', footerOptions: footerOptionsObj };

  /**
   * @method renderSchedulerName
   * @description renders the job name in the scheduler list
   * @param row
   * @returns job name
   */
  const renderSchedulerName = (row: IGetJobDetails) => (
    <p className={classes['job-name']} title={row.jobName} data-testid="jobName">{row.jobName}</p>
  );

  /**
   * @method renderSchedulerStatus
   * @description renders the job status in the scheduler list
   * @param row
   * @returns job status
   */
  const renderSchedulerStatus = (row: IGetJobDetails) => (
    <CIStatusCapsule status={_.startCase(row.jobStatus.toLowerCase())} testId={`${_.startCase(row.jobStatus.toLowerCase())}Status`} />
  );

  /**
   * @method renderFrequency
   * @description renders the job frequency in the scheduler list
   * @param row
   * @returns job frequency
   */
  const renderFrequency = (row: IGetJobDetails) => (<p className={classes['frequency-text']} title={row.frequency}>{row.frequency}</p>);

  /**
   * @method renderUpdatedBy
   * @description renders the name of the person who created or updated the job
   * @param row
   * @returns updatedBy
   */
  const renderUpdatedBy = (row: IGetJobDetails) => (
    <div className={classes['list-modify']} title={row.updatedBy}>{row.updatedBy}</div>
  );

  /**
 * @method renderActionBtn
 * @description renders the action buttons for journey list
 * @param row
 * @returns {view button and edit button}
*/
  const renderActionBtn = (row: IGetJobDetails) => (
    <div className={classes['action-col']}>
      <CIActionBtnGroup
        buttons={[
          {
            label: 'view',
            key: 'viewIcon',
            onClick: () => { },
            testId: 'viewBtn',
            disabled: true,
          },
          {
            label: 'edit',
            key: 'editIcon',
            onClick: () => { },
            testId: 'editBtn',
            disabled: true,
          },
          {
            label: 'delete',
            key: 'deleteIcon',
            onClick: () => deleteScheduler(row.journeyName, row.jobName),
            testId: 'deleteBtn',
            disabled: row.jobStatus.toLowerCase() !== activeStatus,
          },
        ]}
      />
    </div>
  );

  useEffect(() => {
    setApiResponseStatus(DataLoadingState.LOADING);
    const fetchAllScheduledJobs = async () => {
      const resp = await getScheduleList({ journeyName, journeyVersion }, page, item, sortParameter ?? `${SortLabels.SCHEDULER_NAME},${ApiModifySort.DESC}`);
      if (resp.isError) {
        setApiResponseStatus(DataLoadingState.FAILED);
      } else {
        setScheduledJobList(resp.jobDetails);
        setApiResponseStatus(DataLoadingState.COMPLETED);
        setListLength(resp.totalJobCount);
      }
    };
    if (searchTerm) {
      debounceHandler(searchTerm);
    } else {
      fetchAllScheduledJobs();
    }
  }, [item, page, flag, searchTerm, sortParameter]);

  return (
    <div className={classes['schedule-list-container']}>
      <Loader show={apiResponseStatus === DataLoadingState.LOADING} />
      {scheduledJobList.length > 0 && apiResponseStatus !== DataLoadingState.FAILED && (
        <CITableGrid
          headers={[
            {
              name: SortLabels.SCHEDULER_NAME,
              label: translate('job_scheduler_component.txt_job_name'),
              options: { breakPointWidth: 2 },
              sortOptions: {
                callback: jobNameSortToggleHandler
              },
              columnData: {
                field: translate('job_scheduler_component.txt_job_name'), render: renderSchedulerName
              }
            },
            {
              name: SortLabels.SCHEDULER_STATUS,
              label: translate('job_scheduler_component.lbl_status'),
              options: { breakPointWidth: 2 },
              columnData: {
                field: translate('job_scheduler_component.lbl_status'), render: renderSchedulerStatus
              }
            },
            {
              name: SortLabels.SCHEDULER_FREQUENCY,
              label: translate('job_scheduler_component.lbl_frequency'),
              options: { breakPointWidth: 4 },
              sortOptions: {
                callback: frequencySortToggleHandler
              },
              columnData: {
                field: translate('job_scheduler_component.lbl_frequency'), render: renderFrequency, className: classes['list-frequency']
              }
            },
            {
              name: SortLabels.UPDATED_BY,
              label: translate('job_scheduler_component.lbl_updated_by'),
              sortOptions: {
                callback: updatedBySortToggleHandler
              },
              options: { breakPointWidth: 2 },
              columnData: {
                field: SortLabels.UPDATED_BY, render: renderUpdatedBy
              }
            },
            {
              name: SortLabels.ACTION_BTN,
              label: '',
              options: { breakPointWidth: 2 },
              columnData: {
                field: SortLabels.ACTION_BTN, render: renderActionBtn, className: classes['button-alignment']
              }
            }
          ]}
          footer={footerObj}
          sorted={
            { sortedName, sortedOrder }
          }
          tableData={scheduledJobList}
        />
      )}
      {(apiResponseStatus === DataLoadingState.COMPLETED && (scheduledJobList.length === 0)) && (
        <div className={classes['error-container']} data-testid="scheduleListEmpty">
          <NoDataAvailable />
        </div>
      )}
      {apiResponseStatus === DataLoadingState.FAILED && (
        <div className={classes['error-container']} data-testid="scheduleListError">
          {apiStatusCode === 401 ? <Unathourized /> : <UnableToFetch />}
        </div>
      )}
    </div>
  );
};
